import { ReactNode } from 'react';
import { cn } from 'styles';
import { cva, VariantProps } from 'class-variance-authority';

import { ColumnProps, RowColumn } from '../Containers';

const cardVariants = cva('rounded-[1.25rem] bg-card text-card-foreground shadow-sm', {
  variants: {
    variant: {
      border: 'border shadow-sm',
      shadow: 'border border-hover shadow-md',
    },

    spacing: {
      none: '',
      small: 'p-2',
      medium: 'p-4',
      large: 'p-6',
    },
  },
});

type CardVariantProps = VariantProps<typeof cardVariants>;

type CardFlow = 'column' | 'row' | 'none';

type FlowProps<Flow extends CardFlow> = Flow extends 'none' ? unknown : ColumnProps;

export type CardProps<Flow extends CardFlow = 'none'> = {
  className?: string;

  children: ReactNode;

  /**
   * Defines if you want the card to act as a `Row`, `Column` or no control over children
   */
  flow?: Flow;
} & CardVariantProps &
  FlowProps<Flow>;

export function Card<Flow extends CardFlow = 'none'>({
  className,
  children,
  variant = 'border',
  flow = 'none' as Flow,
  spacing = 'medium',
  ...props
}: CardProps<Flow>) {
  if (flow === 'none')
    return <div className={cn(cardVariants({ className, variant, spacing }))}>{children}</div>;

  return (
    <RowColumn
      orientation={flow === 'row' ? 'horizontal' : 'vertical'}
      className={cn(cardVariants({ className, variant, spacing }))}
      {...props}
    >
      {children}
    </RowColumn>
  );
}
